import { IDetailsRowProps } from '@fluentui/react';
import {
  debounce,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  getItemsWithEmptyItemsOfNextPage,
  IDetailsListProps,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { CoreService, ListableFeature } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { getFeatureColumns } from '../FeatureList/FeatureList';
import {
  featureSearchResultListStylesDefault,
  IFeatureSearchResultListClassNames,
  IFeatureSearchResultListStyles,
} from './FeatureSearchResultList.styles';

export interface FeatureSearchResultListData {
  items: ListableFeature[];
  pageToken?: string;
  pageSize?: number;
}

interface IFeatureSearchResultListProps {
  ownedOnly?: boolean;
  searchText?: string;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onOpenFeaturePage: (data: ListableFeature) => void;
}

export const FeatureSearchResultList = ({
  ownedOnly,
  searchText,
  onUpdateData,
  onOpenFeaturePage,
}: IFeatureSearchResultListProps) => {
  const [data, setData] = useState<FeatureSearchResultListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refIsLoadingPage = useRef(false);
  const refPageSize = useRef(defaultPageSize);
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: FeatureSearchResultListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading feature sets...`);
      try {
        const pageSize = refPageSize.current;
        const { listableFeature = [], nextPageToken } = await fsApi(CoreService.SearchFeatures, {
          query: searchText,
          pageSize,
          pageToken,
          ownedOnly,
        });
        const newItems = getItemsWithEmptyItemsOfNextPage(items, listableFeature, pageSize);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize: refPageSize.current,
        };
        setData({
          ...newData,
        });
        onUpdateData && onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.features);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading features: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refIsLoadingPage.current = false;
      }
    },
    [addToast, ownedOnly, searchText, onUpdateData]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    columns: getFeatureColumns(undefined, undefined, undefined, onOpenFeaturePage, true),
    isHeaderVisible: false,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refIsLoadingPage.current) {
        refIsLoadingPage.current = true;
        onLoad(data, false);
      }
      return <ListRow data={item} columns={columns} loading={item.isNotLoaded} />;
    },
  };
  useEffect(() => {
    load();
  }, [load]);
  const classNames = useClassNames<IFeatureSearchResultListStyles, IFeatureSearchResultListClassNames>(
    'FeatureSearchResultList',
    featureSearchResultListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="FeatureSearchResultList">
      {loadingMessage && <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />}
      <div className={classNames.cardView} data-is-scrollable="true">
        <DetailsList {...detailsListProps} />
      </div>
    </div>
  );
};
